import * as THREE from 'three';

// ── Color palette ───────────────────────────────────────────────────────────
const COLOR_STOPS = [
  [0.97, 0.80, 0.64],
  [0.85, 0.61, 0.40],
  [0.56, 0.33, 0.17],
  [0.96, 0.52, 0.68],
  [0.62, 0.36, 0.88],
  [0.08, 0.74, 0.78],
  [0.88, 0.18, 0.22],
  [0.22, 0.21, 0.24],  // dark charcoal (not pure black — would blend with bg)
];

export function getDildoColor(t) {
  const n = COLOR_STOPS.length - 1;
  const fi = Math.min(n - 0.0001, t * n);
  const i = Math.floor(fi);
  const f = fi - i;
  const a = COLOR_STOPS[i];
  const b = COLOR_STOPS[i + 1];
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] + (b[2] - a[2]) * f];
}

export const SWATCH_COLORS_HEX = [
  '#F8CCB0', '#DAA068', '#8F5529', '#F584AC', '#9E5CE0', '#14BDBF', '#E02E38', '#38353D',
];

// ── Bezier helpers ──────────────────────────────────────────────────────────
function bez3(p0, p1, p2, p3, t) {
  const mt = 1 - t;
  return mt*mt*mt*p0 + 3*mt*mt*t*p1 + 3*mt*t*t*p2 + t*t*t*p3;
}
function bez3d(p0, p1, p2, p3, t) {
  const mt = 1 - t;
  return 3*mt*mt*(p1-p0) + 6*mt*t*(p2-p1) + 3*t*t*(p3-p2);
}

// ── Spine ───────────────────────────────────────────────────────────────────
// Spine lies in XY plane, centered at origin (y: -L/2 → +L/2).
// Curvature bends the dildo in X. At t=0 the tangent is always (0,1,0).
function spinePoint(t, L, c) {
  const x = bez3(0, 0, c * L * 0.7, c * L * 0.85, t);
  const y = bez3(-L * 0.5, -L * 0.5 + L * 0.3, L * 0.5 - L * 0.3, L * 0.5, t);
  return [x, y, 0];
}

function spineTangent(t, L, c) {
  const dx = bez3d(0, 0, c * L * 0.7, c * L * 0.85, t);
  const dy = bez3d(-L * 0.5, -L * 0.5 + L * 0.3, L * 0.5 - L * 0.3, L * 0.5, t);
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  return [dx / len, dy / len, 0];
}

// ── Profile radius ──────────────────────────────────────────────────────────
function profileRadius(t, params) {
  const { shaftGirth, glansScale, coronaDepth, taper, baseFlare } = params;
  const G  = shaftGirth * 0.33;           // shaft radius in world units
  const GR = G * glansScale;              // glans radius
  const FR = G * (1 + baseFlare * 0.6);   // flange radius

  const ss = (a, b, x) => {
    const k = Math.max(0, Math.min(1, (x - a) / (b - a)));
    return k * k * (3 - 2 * k);
  };

  if (t < 0.05) return FR;                                             // flat flange
  if (t < 0.15) return FR * (1 - ss(0.05, 0.15, t)) + G * ss(0.05, 0.15, t); // flange→shaft
  if (t < 0.73) return G * (1 - taper * 0.18 * ((t - 0.15) / 0.58)); // shaft taper
  if (t < 0.80) {                                                       // neck below corona
    const G2   = G * (1 - taper * 0.18);
    const neck = G2 * (1 - coronaDepth * 0.12);
    return G2 + (neck - G2) * ss(0.73, 0.80, t);
  }
  if (t < 0.90) {                                                       // corona ridge
    const G2   = G * (1 - taper * 0.18);
    const neck = G2 * (1 - coronaDepth * 0.12);
    const b    = ss(0.80, 0.90, t);
    return neck + (GR - neck) * (b * b * (3 - 2 * b));
  }
  return GR * Math.cos(((t - 0.90) / 0.10) * Math.PI * 0.5);           // glans dome → tip
}

// ── Main geometry (tube + closed bottom cap) ─────────────────────────────────
export function createDildoGeometry(params, spineSegs = 180, ringSegs = 80) {
  const L = params.length * 1.5;
  const c = params.curvature * 0.35;

  const positions = [];
  const uvs = [];
  const indices = [];

  // ── Tube body ──────────────────────────────────────────────────────────────
  for (let i = 0; i <= spineSegs; i++) {
    const t = i / spineSegs;
    const [px, py] = spinePoint(t, L, c);
    const [tx, ty] = spineTangent(t, L, c);
    // Frame: B=(0,0,1), N=B×T=(-ty, tx, 0)
    const nx = -ty, ny = tx;

    const baseR = profileRadius(t, params);

    for (let j = 0; j <= ringSegs; j++) {
      const phi  = (j / ringSegs) * Math.PI * 2;
      const cosP = Math.cos(phi);
      const sinP = Math.sin(phi);

      // Shaft ridges only in the middle section
      let ridgeDisp = 0;
      if (params.ridges > 0.01 && t > 0.17 && t < 0.73) {
        const count = 4 + Math.round(params.ridges * 5);
        const tN    = (t - 0.17) / 0.56;
        ridgeDisp   = params.ridges * 0.04 * baseR *
          Math.max(0, Math.sin(tN * Math.PI * count * params.length));
      }

      const r = Math.max(0.0001, baseR + ridgeDisp);
      positions.push(
        px + r * cosP * nx,
        py + r * cosP * ny,
        r * sinP,
      );
      uvs.push(j / ringSegs, t);
    }
  }

  // Tube triangles
  for (let i = 0; i < spineSegs; i++) {
    for (let j = 0; j < ringSegs; j++) {
      const a  = i       * (ringSegs + 1) + j;
      const b  = a + 1;
      const c2 = (i + 1) * (ringSegs + 1) + j;
      const d  = c2 + 1;
      indices.push(a, c2, b);
      indices.push(b, c2, d);
    }
  }

  // ── Bottom cap (flat disc, closes the base hole) ───────────────────────────
  // At t=0 the tangent is always (0,1,0), so the bottom ring is perfectly
  // horizontal at y = -L/2. We duplicate the ring vertices so the cap can
  // have its own downward-facing normals without disturbing the tube.
  const tubeVerts = (spineSegs + 1) * (ringSegs + 1);

  // t=0 spine/frame (curvature never affects the tangent at t=0)
  const py0 = -L * 0.5; // spine y at base
  const capR = profileRadius(0, params); // = FR (flat flange radius)
  const nx0  = -1;       // always: tangent=(0,1,0) → N=(-1,0,0)

  // Center vertex
  const centerIdx = tubeVerts;
  positions.push(0, py0, 0);
  uvs.push(0.5, 0.5);

  // Perimeter ring (separate vertices from tube)
  const capRingStart = centerIdx + 1;
  for (let j = 0; j <= ringSegs; j++) {
    const phi  = (j / ringSegs) * Math.PI * 2;
    const cosP = Math.cos(phi);
    const sinP = Math.sin(phi);
    positions.push(
      capR * cosP * nx0,  // = -capR*cosP   (matches tube i=0 ring)
      py0,
      capR * sinP,
    );
    uvs.push(0.5 + 0.5 * cosP, 0.5 + 0.5 * sinP);
  }

  // Fan: (center, j+1, j)  → cross product gives normal in −Y direction ✓
  for (let j = 0; j < ringSegs; j++) {
    indices.push(centerIdx, capRingStart + j + 1, capRingStart + j);
  }

  // ── Build BufferGeometry ───────────────────────────────────────────────────
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geom.setAttribute('uv',       new THREE.Float32BufferAttribute(uvs,       2));
  geom.setIndex(indices);
  geom.computeVertexNormals();

  // Smooth seam normals along the tube (j=0 and j=ringSegs share same position)
  const normals = geom.getAttribute('normal');
  for (let i = 0; i <= spineSegs; i++) {
    const i0   = i * (ringSegs + 1) + 0;
    const iN   = i * (ringSegs + 1) + ringSegs;
    const avgX = (normals.getX(i0) + normals.getX(iN)) * 0.5;
    const avgY = (normals.getY(i0) + normals.getY(iN)) * 0.5;
    const avgZ = (normals.getZ(i0) + normals.getZ(iN)) * 0.5;
    const len  = Math.sqrt(avgX*avgX + avgY*avgY + avgZ*avgZ) || 1;
    normals.setXYZ(i0, avgX/len, avgY/len, avgZ/len);
    normals.setXYZ(iN, avgX/len, avgY/len, avgZ/len);
  }
  normals.needsUpdate = true;

  return geom;
}
